// ILF Product Upsell — <ilf-product-upsell-item>
// One row of the "Pairs well with" block. Adds its variant to the cart via
// IlfCart (assets/ilf-commerce.js): the cart request, the theme's drawer and
// header-bubble refresh, and the 'ilf:cart:added' / 'ilf:cart:error' events
// all live in the runtime, so this element stays theme-agnostic. All visual
// states (loading spinner, added check, error row) are CSS classes — JS
// toggles classes and text content only, never styles.

if (!customElements.get('ilf-product-upsell-item')) {
  class IlfProductUpsellItem extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.errorEl = this.querySelector('.item-error');
      this.onAdd = this.onAdd.bind(this);
      // A row can carry several add buttons (one per variant for
      // single-option products); each holds its own variant id and state.
      this.buttons = [...this.querySelectorAll('[data-add-button]')];
      this.buttons.forEach((button) => button.addEventListener('click', this.onAdd));
      this.addedTimers = new Map();
    }

    disconnectedCallback() {
      this.buttons?.forEach((button) => button.removeEventListener('click', this.onAdd));
      this.addedTimers?.forEach((timer) => clearTimeout(timer));
      this.initialised = false;
    }

    onAdd(event) {
      const button = event.currentTarget;
      if (button.classList.contains('is-loading')) return;

      // Variant id comes from the button (single-variant products) or from
      // the row's variant dropdown (single-option products).
      const select = this.querySelector('.variant-select');
      const variantId = parseInt(button.dataset.variantId || select?.value, 10);
      if (!variantId) return;

      this.clearError();
      button.classList.add('is-loading');
      button.setAttribute('disabled', '');

      window.IlfCart.add({ items: [{ id: variantId, quantity: 1 }] }, { source: 'ilf-product-upsell' })
        .then(() => {
          this.stopLoading(button);
          this.showAdded(button);
        })
        .catch((error) => {
          this.stopLoading(button);
          // error.response is set for cart-level rejections (sold out, cart
          // limits); anything else is a network failure.
          if (error.response) {
            this.showError(error.message);
          } else {
            this.showError('Could not add to cart. Check your connection and try again.');
          }
        });
    }

    stopLoading(button) {
      button.classList.remove('is-loading');
      button.removeAttribute('disabled');
    }

    showAdded(button) {
      button.classList.add('is-added');
      clearTimeout(this.addedTimers.get(button));
      // Revert to "+ Add" so the item can be added again.
      this.addedTimers.set(
        button,
        setTimeout(() => button.classList.remove('is-added'), 2000)
      );
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.classList.add('has-error');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.classList.remove('has-error');
    }
  }

  customElements.define('ilf-product-upsell-item', IlfProductUpsellItem);
}

// <ilf-product-upsell-slider> — pages the upsell rows. Each slide is a page of
// products; the arrows live in the block header. With a single page the
// arrows lock (watchOverflow) and the slider is inert.
if (!customElements.get('ilf-product-upsell-slider')) {
  class IlfProductUpsellSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      if (typeof Swiper === 'undefined') {
        window.addEventListener('load', () => this.connectedCallback(), { once: true });
        return;
      }
      this.initialised = true;

      const block = this.closest('.ilf-product-upsell');
      this.swiper = new Swiper(this, {
        slidesPerView: 1,
        spaceBetween: 12,
        autoHeight: true,
        speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
        watchOverflow: true,
        // Rows are links/buttons edge to edge; allow swipes to start on them
        // (clicks survive — Swiper cancels the click only after real movement).
        focusableElements: 'input, select, option, textarea, video, label',
        navigation: {
          prevEl: block?.querySelector('.upsell-nav-btn--prev'),
          nextEl: block?.querySelector('.upsell-nav-btn--next'),
        },
        pagination: {
          el: block?.querySelector('.upsell-dots'),
          clickable: true,
        },
        a11y: { enabled: true },
      });
    }

    disconnectedCallback() {
      this.swiper?.destroy(true, true);
      this.swiper = null;
      this.initialised = false;
    }
  }

  customElements.define('ilf-product-upsell-slider', IlfProductUpsellSlider);
}
